import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import HeaderSearch from "../../components/HeaderSearch";
import GridAddButton from "../../components/GridAddButton";
import GridPhotoItem from "../../components/GridPhotoItem";
import { sendToApi } from "../../common/common.rekognition";
import { findAllRekognitionMatch } from "../../common/common.rekognition.matcher";
import Loader from "../../components/Loader";
import { useIntl } from "react-intl";
import { useGlobalData } from "../../context/GlobalContext";
import { useFileStore } from "../../stores/fileStore";
import { useGetFindReports } from "../../hooks/useGetFindReports";
import TestCrash from "../../components/TestCrash";
import { toast } from "react-toastify";
import useNavigatorOnLine from "../../hooks/useNavigatorOnLine";

const Home = () => {
	const intl = useIntl();
	const navigate = useNavigate();
	const location = useLocation();
	const navigatorOnline = useNavigatorOnLine();

	const { data: reports, isLoading, isError, refetch, isFetching } = useGetFindReports();

	const { categories } = useGlobalData();

	const [processingPhoto, setProcessingPhoto] = useState(false);
	const [searchText, setSearchText] = useState("");

	useEffect(() => {
		if (location.state?.refetch) {
			refetch();
		}
	}, [location.state]);

	const handleSearch = (text: string) => {
		setSearchText(text);
	};

	const handleHomeDataReload = async () => {
		await refetch();
	};

	const handlePhotoSelect = async (file?: File) => {
		if (file && categories) {
			try {
				setProcessingPhoto(true);
				const rekognitionData = await sendToApi(file);

				if (rekognitionData === "network-error") {
					toast.error(intl.formatMessage({ id: "network_error_rekognition" }));
					setProcessingPhoto(false);
					return;
				}

				const allMatches = findAllRekognitionMatch(rekognitionData, categories);
				setProcessingPhoto(false);

				const fileUrl = URL.createObjectURL(file);

				useFileStore.getState().setFile(file);

				navigate("/backoffice/create-report", {
					state: { fileUrl: fileUrl, matches: allMatches },
				});
			} catch (error) {
				console.error("Image processing failed", error);
			} finally {
				setProcessingPhoto(false);
			}
		}
	};

	// Filter reports based on search text
	const filteredReports = reports?.filter((report) =>
		report.findReportReferenceNumber.toLowerCase().includes(searchText.toLowerCase()),
	);

	return (
		<>
			<div className="container mt-3">
				{/* START UNCOMMENT WHEN NEED TO SEE CRASH FALLBACK COMPONENT */}
				{/* <TestCrash /> */}
				{/* END UNCOMMENT WHEN NEED TO SEE CRASH FALLBACK COMPONENT */}

				{!navigatorOnline ? (
					<div className="alert alert-warning text-center" role="alert">
						<i className="bi bi-wifi-off"></i> {intl.formatMessage({ id: "you_are_offline" })}
					</div>
				) : null}

				<div className="d-flex align-items-center gap-2">
					<HeaderSearch
						isLoading={isFetching}
						handleHomeDataReload={handleHomeDataReload}
						handleSearch={handleSearch}
					/>
				</div>
				{isError ? (
					<div className="alert alert-danger mt-2" role="alert">
						{intl.formatMessage({ id: "loading_reports_error" })}
					</div>
				) : null}
				{isLoading ? (
					<div className="container mt-3">
						<Loader message={intl.formatMessage({ id: "loading_reports" })} />
					</div>
				) : (
					<div>
						<div className="row p-2">
							{processingPhoto ? (
								<div className="col-12 text-center">
									<Loader message={intl.formatMessage({ id: "processing_photo" })} />
								</div>
							) : (
								<>
									<GridAddButton handlePhotoSelect={handlePhotoSelect} />
									{filteredReports?.map((report) => (
										<GridPhotoItem
											additionalData={{
												matchCount: report.matchCount,
											}}
											key={report.findReportID}
											src={report.lossObjectImages[0]}
											label={report.findReportReferenceNumber}
											handleClick={() => {
												navigate(`/backoffice/report/${report.findReportID}`);
											}}
										/>
									))}
								</>
							)}
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default Home;
